import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, X } from "lucide-react";
import { controlApi } from "./api";
import type { AuditLogItem } from "./types";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field } from "../applications/shared/InfoBlock";
import { formatDateTime } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useDebounce } from "@/lib/utils/useDebounce";

const TARGET_OPTIONS = [
  { value: "user", label: "Usuarios" },
  { value: "role", label: "Roles" },
  { value: "settlement", label: "Liquidaciones" },
  { value: "event", label: "Eventos" },
  { value: "producer_application", label: "Postulación productor" },
  { value: "artist_application", label: "Postulación artista" },
];

const PAGE_SIZE = 50;

export function AuditScreen() {
  const [search, setSearch] = useState("");
  const [targetType, setTargetType] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, targetType]);

  const query = useQuery({
    queryKey: ["nexus-control", "audit", { q: debouncedSearch, targetType, page }],
    queryFn: () =>
      controlApi.listAudit({
        q: debouncedSearch || undefined,
        targetType: targetType || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  const selected = useMemo(
    () =>
      selectedId !== null
        ? (query.data?.items ?? []).find((i) => i.id === selectedId) ?? null
        : null,
    [query.data, selectedId],
  );

  const columns: Column<AuditLogItem & { id: string }>[] = [
    {
      key: "when",
      header: "Cuándo",
      cell: (r) => <span className="text-fg-subtle text-xs">{formatDateTime(r.createdAt)}</span>,
      width: "min",
    },
    {
      key: "actor",
      header: "Actor",
      cell: (r) =>
        r.actor ? (
          <div>
            <div className="text-xs text-fg">{r.actor.name}</div>
            <div className="text-2xs text-fg-subtle">{r.actor.email}</div>
          </div>
        ) : (
          <span className="text-fg-subtle text-xs italic">Sistema</span>
        ),
    },
    {
      key: "action",
      header: "Acción",
      cell: (r) => (
        <code className="text-2xs text-fg bg-bg-subtle px-1.5 py-0.5 rounded">{r.action}</code>
      ),
    },
    {
      key: "target",
      header: "Objetivo",
      cell: (r) =>
        r.targetType ? (
          <span className="text-2xs text-fg-muted">
            {r.targetType}
            {r.targetId && <span className="text-fg-subtle"> · #{r.targetId}</span>}
          </span>
        ) : (
          <span className="text-fg-subtle text-xs">—</span>
        ),
    },
    {
      key: "ip",
      header: "IP",
      cell: (r) => <span className="text-fg-subtle text-2xs font-mono">{r.ip ?? "—"}</span>,
      width: "min",
    },
  ];

  // Type narrowing — el id viene como string (uuid), ApplicationsTable necesita { id: number }
  // pero el constraint es relajado a number. Adapter:
  const rowsAdapted = (query.data?.items ?? []).map((it) => ({
    ...it,
    id: it.id as unknown as number, // string uuid; el componente sólo lo usa como key, OK
  }));

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Nexus Control · Audit</p>
          <h1 className="nx-h1">Audit log</h1>
          <p className="text-sm text-fg-muted mt-1">
            Bitácora append-only de toda acción modificadora dentro de Nexus.
            Cada entry tiene actor, action slug, objetivo opcional, payload de
            contexto, IP y user-agent.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por action, actor o objetivo…"
        status={targetType}
        onStatusChange={setTargetType}
        statusOptions={TARGET_OPTIONS}
      />

      {query.isLoading ? (
        <LoadingBlock label="Cargando audit log…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable
          rows={rowsAdapted}
          columns={columns as any}
          page={query.data?.page ?? 1}
          pageSize={query.data?.pageSize ?? PAGE_SIZE}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id as unknown as string)}
          selectedId={selectedId as unknown as number}
          emptyLabel="Sin entries que coincidan con los filtros."
        />
      )}

      {selected && <AuditDrawer item={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

function AuditDrawer({ item, onClose }: { item: AuditLogItem; onClose: () => void }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate font-mono text-sm">{item.action}</h2>
            <p className="text-2xs text-fg-subtle mt-0.5">{formatDateTime(item.createdAt)}</p>
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          <Section title="Actor">
            <Field
              label="Quién"
              value={
                item.actor ? (
                  <>
                    <div>{item.actor.name}</div>
                    <a
                      className="text-accent hover:underline text-2xs"
                      href={`mailto:${item.actor.email}`}
                    >
                      {item.actor.email}
                    </a>
                  </>
                ) : (
                  "Sistema"
                )
              }
              wide
            />
            {item.actorUserId && <Field label="User ID" value={item.actorUserId} />}
          </Section>

          {(item.targetType || item.targetId) && (
            <Section title="Objetivo">
              <Field label="Tipo" value={item.targetType ?? "—"} />
              <Field label="ID" value={item.targetId ?? "—"} />
            </Section>
          )}

          {item.payload && Object.keys(item.payload).length > 0 && (
            <Section title="Payload">
              <Field
                label="Contexto"
                value={
                  <pre className="text-2xs bg-bg-subtle border border-border-subtle rounded p-2 overflow-auto max-h-72">
                    {JSON.stringify(item.payload, null, 2)}
                  </pre>
                }
                wide
              />
            </Section>
          )}

          <Section title="Request">
            <Field label="IP" value={item.ip ?? "—"} />
            <Field
              label="User agent"
              value={<span className="text-2xs break-all">{item.userAgent ?? "—"}</span>}
              wide
            />
          </Section>

          <Section title="Identificador">
            <Field
              label="UUID"
              value={<code className="text-2xs break-all">{item.id}</code>}
              wide
            />
          </Section>
        </div>
      </aside>
    </>
  );
}

// Sin uso por el momento — placeholder
export type { AuditLogItem };
